import React, { useState, useMemo, useEffect, useCallback } from "react";
import { AdViewerSheet } from "@/Ads/components/AdViewerSheet";
import { getStoreStatus } from "../lib/format-hours";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useLocations } from "../hooks/use-locations";
import { useUserLocation } from "../hooks/use-user-location";
import { useGeolocation, calculateDistance } from "../hooks/use-geolocation";
import { formatLocationHours, formatTime } from "../lib/format-hours";
import { isCurrentlyActive } from "@/lib/date-utils";
import { BASEMAP_ATTRIBUTION, BASEMAP_MAX_ZOOM, BASEMAP_TILE_URL } from "@/lib/basemap";
import { Location, LocationAd, LocationService, RichTextBlock } from "../types/locations";
import { Sheet as ServiceSheet, SheetContent as ServiceSheetContent, SheetHeader as ServiceSheetHeader, SheetTitle as ServiceSheetTitle, SheetDescription as ServiceSheetDescription } from "@/components/ui/sheet";
import { RichTextRenderer } from "@/components/RichTextRenderer";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import { renderToStaticMarkup } from 'react-dom/server';
import { IconBrandFacebook, IconBrandInstagram, IconBrandLinkedin, IconBrandPinterest, IconBrandTiktok, IconBrandX, IconBrandYoutube, IconCheck, IconClock, IconCurrentLocation, IconExternalLink, IconFileText, IconInfoCircle, IconMail, IconMapPin, IconMapPinFilled, IconNavigation, IconPhone, IconSearch, IconShoppingCart, IconTag, IconX, type TablerIcon } from '@tabler/icons-react';

// Helper to extract text from rich text children
const extractHeadingText = (block: RichTextBlock): string => {
  return block.children
    .map((child) => {
      if ('text' in child) {
        return (child as { text: string }).text;
      }
      return '';
    })
    .join('');
};

// Parse rich text content into sections split by headings
interface ContentSection {
  title: string;
  content: RichTextBlock[];
}

const parseContentSections = (content: RichTextBlock[] | string | undefined): ContentSection[] => {
  if (!content || typeof content === 'string' || !Array.isArray(content)) {
    return [];
  }

  const sections: ContentSection[] = [];
  let currentSection: ContentSection | null = null;

  for (const block of content) {
    if (block.type === 'heading') {
      // Start a new section
      if (currentSection) {
        sections.push(currentSection);
      }
      currentSection = {
        title: extractHeadingText(block),
        content: [], // Don't include the heading itself in content - it's the tab title
      };
    } else if (currentSection) {
      // Add content to current section
      currentSection.content.push(block);
    } else {
      // Content before any heading - create an "Overview" section
      if (sections.length === 0 && !currentSection) {
        currentSection = {
          title: 'Overview',
          content: [block],
        };
      }
    }
  }

  // Push the last section
  if (currentSection) {
    sections.push(currentSection);
  }

  return sections;
};

const getLinkInfo = (url: string): { icon: TablerIcon; label: string } => {
  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com')) {
    return { icon: IconBrandFacebook, label: 'Facebook' };
  }
  if (lowerUrl.includes('instagram.com')) {
    return { icon: IconBrandInstagram, label: 'Instagram' };
  }
  if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) {
    return { icon: IconBrandX, label: 'X / Twitter' };
  }
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return { icon: IconBrandYoutube, label: 'YouTube' };
  }
  if (lowerUrl.includes('linkedin.com')) {
    return { icon: IconBrandLinkedin, label: 'LinkedIn' };
  }
  if (lowerUrl.includes('tiktok.com')) {
    return { icon: IconBrandTiktok, label: 'TikTok' };
  }
  if (lowerUrl.includes('pinterest.com')) {
    return { icon: IconBrandPinterest, label: 'Pinterest' };
  }
  if (lowerUrl.includes('mailto:')) {
    return { icon: IconMail, label: 'Email' };
  }
  if (lowerUrl.includes('shop') || lowerUrl.includes('instacart.com') || lowerUrl.includes('egrowcery.com')|| lowerUrl.includes('doordash.com') || lowerUrl.includes('store') || lowerUrl.includes('cart') || lowerUrl.includes('order')) {
    return { icon: IconShoppingCart, label: 'Shop Online' };
  }

  return { icon: IconExternalLink, label: 'Visit Site' };
};

const DEFAULT_CENTER: [number, number] = [47.6062, -122.3321];

// Render the Tabler pin to static markup for the Leaflet divIcon
const getMarkerSvg = (isHighlighted = false) => {
  const size = isHighlighted ? 40 : 32;
  return renderToStaticMarkup(
    <IconMapPinFilled
      size={size}
      style={{ color: 'hsl(var(--primary))', fill: 'currentColor' }}
    />,
  );
};

interface StoreSelectorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialView?: 'stores' | 'details';
}

interface LocationWithCoords extends Location {
  coordinates?: [number, number];
  distance?: number;
  addressString?: string;
  locationKey: string;
}

const createCustomIcon = (isHighlighted = false) => {
  const size = isHighlighted ? 40 : 32;
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="marker-container" style="${isHighlighted ? 'filter: drop-shadow(0 0 8px hsl(var(--primary)));' : ''}">
        ${getMarkerSvg(isHighlighted)}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: () => string })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const MapViewUpdater = ({ center, activeLocationId }: { center: [number, number]; activeLocationId: string | null }) => {
  const map = useMap();
  const prevCenterRef = React.useRef<[number, number] | null>(null);
  const prevSelectedIdRef = React.useRef<string | null>(null);
  
  useEffect(() => {
    // Always update if activeLocationId changed (user selected a location)
    // Or if center coordinates actually changed
    const centerChanged = !prevCenterRef.current || 
        prevCenterRef.current[0] !== center[0] || 
        prevCenterRef.current[1] !== center[1];
    const selectionChanged = activeLocationId !== null && activeLocationId !== prevSelectedIdRef.current;
    
    if (centerChanged || selectionChanged) {
      // Get current zoom level to maintain it
      const currentZoom = map.getZoom();
      map.setView(center, currentZoom, { animate: true });
      prevCenterRef.current = center;
      if (activeLocationId) {
        prevSelectedIdRef.current = activeLocationId;
      }
    }
  }, [map, center, activeLocationId]);
  
  return null;
};

// Component to handle marker icon updates without unmounting
const UpdatableMarker = ({ 
  position, 
  isActive, 
  location, 
  onMarkerClick 
}: { 
  position: [number, number]; 
  isActive: boolean; 
  location: LocationWithCoords;
  onMarkerClick: () => void;
}) => {
  const markerRef = React.useRef<L.Marker | null>(null);
  const icon = useMemo(() => createCustomIcon(false), []);
  
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setIcon(icon);
    }
  }, [icon]);
  
  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={icon}
      eventHandlers={{ 
        click: onMarkerClick,
      }}
    >
      <Popup className="custom-popup" closeButton={true}>
        <div className="text-sm space-y-1 min-w-[180px]">
          <p className="font-semibold text-foreground">{location.LocationTitle}</p>
          <p className="text-muted-foreground text-xs">{location.LocationAddress.LocationAddressLineOne}</p>
          {location.distance && (
            <p className="text-xs text-primary font-medium mt-2 flex items-center gap-1">
              <IconMapPin className="h-3 w-3" />
              {location.distance.toFixed(1)} mi away
            </p>
          )}
        </div>
      </Popup>
    </Marker>
  );
};

export const StoreSelectorSheet = ({ open, onOpenChange, initialView = 'stores' }: StoreSelectorSheetProps) => {
  const [adViewerOpen, setAdViewerOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<LocationAd | null>(null);
  const [serviceSheetOpen, setServiceSheetOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<LocationService | null>(null);
  const [detailsMapCenter, setDetailsMapCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [isDetailsGeocoding, setIsDetailsGeocoding] = useState(false);

  const getLocationKey = useCallback((location: Location) => {
    const lineOne = location.LocationAddress.LocationAddressLineOne || '';
    const zip = location.LocationAddress.LocationAddressZipCode || '';
    const siteId = location.site?.SiteID || '';
    return `${location.LocationTitle}__${lineOne}__${zip}__${siteId}`;
  }, []);

  const { data: locations, isLoading } = useLocations();
  const { selectedLocation: persistedLocation, setLocation, isSelected, clearLocation } = useUserLocation();
  const { position: userPosition, getCurrentPosition, isLoading: isGeolocating } = useGeolocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<'stores' | 'details'>(initialView);
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [activeLocationId, setActiveLocationId] = useState<string | null>(persistedLocation ?? null);
  const [geocodedCoordinates, setGeocodedCoordinates] = useState<Map<string, [number, number]>>(new Map());
  const [activeCoordinates, setActiveCoordinates] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    } else {
      // Use the initialView prop passed from the caller
      setViewMode(initialView);
    }
  }, [open, initialView]);

  const locationsWithCoords = useMemo<LocationWithCoords[]>(() => {
    if (!locations?.length) return [];
    
    return locations.map((location) => {
      const locationKey = getLocationKey(location);
      const addressString = [
        location.LocationAddress.LocationAddressLineOne,
        location.LocationAddress.LocationAddressCity,
        location.LocationAddress.LocationAddressState,
        location.LocationAddress.LocationAddressZipCode,
      ].filter(Boolean).join(", ");
      
      // Get coordinates from geocoded map if available
      const coordinates: [number, number] | undefined = geocodedCoordinates.get(locationKey);

      const distance = userPosition && coordinates
        ? calculateDistance(userPosition.latitude, userPosition.longitude, coordinates[0], coordinates[1])
        : undefined;

      return { ...location, coordinates, distance, addressString, locationKey };
    });
  }, [locations, userPosition, geocodedCoordinates, getLocationKey]);

  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return locationsWithCoords;
    
    const query = searchQuery.toLowerCase();
    return locationsWithCoords.filter((location) => {
      const address = [
        location.LocationAddress.LocationAddressLineOne,
        location.LocationAddress.LocationAddressCity,
        location.LocationAddress.LocationAddressState,
        location.LocationAddress.LocationAddressZipCode,
      ].join(" ").toLowerCase();
      
      return location.LocationTitle.toLowerCase().includes(query) || address.includes(query);
    });
  }, [locationsWithCoords, searchQuery]);

  const sortedLocations = useMemo(() => {
    return [...filteredLocations].sort((a, b) => {
      // Sort alphabetically by title first
      const titleCompare = a.LocationTitle.localeCompare(b.LocationTitle);
      if (titleCompare !== 0) return titleCompare;

      // Then sort by distance if available (as tiebreaker)
      if (userPosition) {
        const distA = a.distance ?? Infinity;
        const distB = b.distance ?? Infinity;
        return distA - distB;
      }
      return 0;
    });
  }, [filteredLocations, userPosition]);

  const activeLocation = useMemo(() => {
    if (!activeLocationId) return null;
    return (
      sortedLocations.find((location) => location.locationKey === activeLocationId) ??
      sortedLocations.find((location) => location.LocationTitle === activeLocationId) ??
      locationsWithCoords.find((location) => location.locationKey === activeLocationId) ??
      locationsWithCoords.find((location) => location.LocationTitle === activeLocationId) ??
      null
    );
  }, [activeLocationId, sortedLocations, locationsWithCoords]);

  const selectedLocationData = useMemo(() => {
    if (!persistedLocation || !locations) return null;
    return locations.find((loc) => loc.LocationTitle === persistedLocation) || null;
  }, [persistedLocation, locations]);

  const hasSelectedLocation = Boolean(selectedLocationData);

  // Sync active location with persisted selection when available
  useEffect(() => {
    if (persistedLocation) {
      setActiveLocationId(persistedLocation);
    }
  }, [persistedLocation]);

  // Ensure there is always an active location when data/search changes
  useEffect(() => {
    if (!sortedLocations.length) {
      if (activeLocationId !== null) {
        setActiveLocationId(null);
      }
      return;
    }

    if (!activeLocationId) {
      setActiveLocationId(sortedLocations[0].locationKey);
      return;
    }

    const isActiveInList = sortedLocations.some(
      (location) => location.locationKey === activeLocationId || location.LocationTitle === activeLocationId
    );

    if (!isActiveInList) {
      setActiveLocationId(sortedLocations[0].locationKey);
    }
  }, [sortedLocations, activeLocationId]);

  // Guard detail view: if no selected location data, force back to stores view
  useEffect(() => {
    if (viewMode === 'details' && !selectedLocationData) {
      setViewMode('stores');
    }
  }, [viewMode, selectedLocationData]);

  // Geocode for details map when viewing details
  useEffect(() => {
    if (viewMode !== 'details' || !selectedLocationData) return;

    const addressString = [
      selectedLocationData.LocationAddress.LocationAddressLineOne,
      selectedLocationData.LocationAddress.LocationAddressCity,
      selectedLocationData.LocationAddress.LocationAddressState,
      selectedLocationData.LocationAddress.LocationAddressZipCode,
    ].filter(Boolean).join(', ');

    if (!addressString) return;

    setIsDetailsGeocoding(true);
    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressString)}&limit=1`, {
      headers: { 'User-Agent': 'StoreLocator/1.0' }
    })
      .then(res => res.json())
      .then(data => {
        if (data?.length > 0) {
          const coords: [number, number] = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
          setDetailsMapCenter(coords);
        }
      })
      .catch(error => {
        console.error('Geocoding error:', error);
      })
      .finally(() => {
        setIsDetailsGeocoding(false);
      });
  }, [viewMode, selectedLocationData]);

  // Center the map whenever the active location changes
  useEffect(() => {
    if (!activeLocationId) return;

    const targetLocation =
      sortedLocations.find((location) => location.locationKey === activeLocationId || location.LocationTitle === activeLocationId) ??
      locationsWithCoords.find((location) => location.locationKey === activeLocationId || location.LocationTitle === activeLocationId);

    if (!targetLocation) return;

    // Clear active coordinates until we resolve or retrieve cached coords for this location
    setActiveCoordinates(null);

    const cachedCoords =
      geocodedCoordinates.get(targetLocation.locationKey) ?? targetLocation.coordinates;

    if (cachedCoords) {
      setActiveCoordinates(cachedCoords);
      setMapCenter(cachedCoords);
      return;
    }

    if (!targetLocation.addressString) return;

    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(targetLocation.addressString)}&limit=1`, {
      headers: { 'User-Agent': 'StoreLocator/1.0' }
    })
      .then(res => res.json())
      .then(data => {
        if (data?.length > 0) {
          const coords: [number, number] = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
          setGeocodedCoordinates(prev => {
            const next = new Map(prev);
            next.set(targetLocation.locationKey, coords);
            return next;
          });
          setActiveCoordinates(coords);
          setMapCenter(coords);
        }
      })
      .catch(error => {
        console.error('Geocoding error:', error);
      });
  }, [activeLocationId, sortedLocations, locationsWithCoords, geocodedCoordinates]);

  const focusLocation = useCallback(
    (location: LocationWithCoords, options?: { persist?: boolean }) => {
      setActiveLocationId(location.locationKey);
      setActiveCoordinates(location.coordinates ?? null);
      if (options?.persist) {
        // Pass the full location object to store LocationCouponID for coupon API
        setLocation(location);
      }
    },
    [setLocation]
  );

  const handleUseCurrentLocation = () => {
    getCurrentPosition();
  };

  const activeMarkerPosition = activeCoordinates ?? activeLocation?.coordinates ?? mapCenter;

  const storeSelectionContent = (
    <>
      {/* Search Controls */}
      <div className="px-6 pb-4 pt-2 space-y-3 border-b">
        <div className="relative">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Enter zip code or city"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
              onClick={() => setSearchQuery("")}
            >
              <IconX className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleUseCurrentLocation}
          disabled={isGeolocating}
          className="w-full"
        >
          <IconCurrentLocation className="h-4 w-4 mr-2" />
          {isGeolocating ? "Locating..." : "Use my current location"}
        </Button>
      </div>

      {/* Map Card */}
      <div className="px-6 pt-6 pb-4">
        <Card className="overflow-hidden rounded-lg border shadow-sm">
          {isLoading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : (
            <div className="h-[300px] w-full relative">
              <MapContainer
                center={mapCenter}
                zoom={15}
                style={{ height: "100%", width: "100%" }}
                zoomControl={true}
                className="rounded-lg"
              >
                <MapViewUpdater center={mapCenter} activeLocationId={activeLocationId} />
                <TileLayer
                  attribution={BASEMAP_ATTRIBUTION}
                  url={BASEMAP_TILE_URL}
                  maxZoom={BASEMAP_MAX_ZOOM}
                />
                {activeLocation && (
                  <Marker
                    position={activeMarkerPosition}
                    icon={createCustomIcon()}
                  >
                    <Popup className="custom-popup" closeButton={true}>
                      <div className="text-sm space-y-1 min-w-[180px]">
                        <p className="font-semibold text-foreground">
                          {activeLocation.LocationTitle}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {activeLocation.LocationAddress.LocationAddressLineOne}
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Store Cards List */}
      <div className="px-6 pb-6">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-4">
                <Skeleton className="h-6 w-3/4 mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-2/3 mb-4" />
                <Skeleton className="h-9 w-full" />
              </Card>
            ))}
          </div>
        ) : sortedLocations.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No stores found
          </div>
        ) : (
          <div className="space-y-4">
            {sortedLocations.map((location, index) => {
              const isActiveLocation =
                activeLocationId === location.locationKey || activeLocationId === location.LocationTitle;
              const isSavedLocation = isSelected(location.LocationTitle);
              const storeStatus = getStoreStatus(location.LocationHours);
              const fullAddress = [
                location.LocationAddress.LocationAddressLineOne,
                location.LocationAddress.LocationAddressLineTwo,
                `${location.LocationAddress.LocationAddressCity}, ${location.LocationAddress.LocationAddressState} ${location.LocationAddress.LocationAddressZipCode}`,
              ]
                .filter(Boolean)
                .join(', ');

              return (
                <Card
                  key={index}
                  className={`relative transition-all cursor-pointer hover:shadow-md ${
                    isActiveLocation ? "ring-2 ring-primary" : ""
                  }`}
                  onClick={() => focusLocation(location)}
                >
                  <div className="p-4 pb-3">
                    <Badge
                      className={`w-fit mb-2 text-xs ${storeStatus.isOpen ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'}`}
                    >
                      <IconClock className="h-3 w-3 mr-1" />
                      {storeStatus.message}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-foreground">{location.LocationTitle}</h3>
                      {isSavedLocation && (
                        <Badge variant="default" className="text-xs">
                          <IconCheck className="h-3 w-3 mr-1" />
                          Selected
                        </Badge>
                      )}
                    </div>
                    {location.LocationBanner && (
                      <p className="text-sm text-muted-foreground -mt-1">{location.LocationBanner}</p>
                    )}
                  </div>

                  <div className="px-4 pb-4 space-y-3">
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <IconMapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{fullAddress}</span>
                    </div>

                    <div className="flex items-center gap-2 text-muted-foreground">
                      <IconPhone className="h-4 w-4 flex-shrink-0" />
                      <span className="text-sm">{location.LocationPhoneNumber}</span>
                    </div>

                    {location.distance && (
                      <div className="flex items-center gap-2 text-emerald-500">
                        <IconNavigation className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm">{location.distance.toFixed(1)} mi away</span>
                      </div>
                    )}

                    <Button
                      variant={isSavedLocation ? "destructive" : "default"}
                      size="sm"
                      className="w-full mt-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isSavedLocation) {
                          clearLocation();
                        } else {
                          focusLocation(location, { persist: true });
                        }
                      }}
                    >
                      {isSavedLocation ? "Clear Selection" : "Set as My Store"}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-full sm:w-[500px] p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="text-2xl">Select your store</SheetTitle>
        </SheetHeader>

        {hasSelectedLocation ? (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="px-6 py-4 border-b flex-shrink-0">
              <div className="w-full flex justify-center">
                <div className="bg-muted rounded-md p-1 flex gap-1">
                  <button
                    type="button"
                    onClick={() => setViewMode('stores')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${
                      viewMode === 'stores'
                        ? 'bg-background shadow-sm text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Store Selection
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('details')}
                    disabled={!selectedLocationData}
                    className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      viewMode === 'details'
                        ? 'bg-background shadow-sm text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Store Details
                  </button>
                </div>
              </div>
            </div>

            {viewMode === 'details' && (
              <div className="flex-1 overflow-y-auto">
              {selectedLocationData && (() => {
                const formattedHours = formatLocationHours(selectedLocationData.LocationHours);
                const fullAddress = [
                  selectedLocationData.LocationAddress.LocationAddressLineOne,
                  selectedLocationData.LocationAddress.LocationAddressLineTwo,
                  `${selectedLocationData.LocationAddress.LocationAddressCity}, ${selectedLocationData.LocationAddress.LocationAddressState} ${selectedLocationData.LocationAddress.LocationAddressZipCode}`,
                ]
                  .filter(Boolean)
                  .join(', ');

                return (
                  <div className="px-6 py-4">
                  {/* Header */}
                  <div className="space-y-1 mb-4">
                    <h3 className="text-2xl font-bold text-foreground">{selectedLocationData.LocationTitle}</h3>
                    {selectedLocationData.LocationBanner && (
                      <p className="text-base text-muted-foreground">{selectedLocationData.LocationBanner}</p>
                    )}
                  </div>

                  {/* Map */}
                  <Card className="overflow-hidden rounded-lg border shadow-sm mb-6">
                    {isDetailsGeocoding ? (
                      <Skeleton className="h-[200px] w-full" />
                    ) : (
                      <div className="h-[200px] w-full relative">
                        <MapContainer
                          key={`details-${detailsMapCenter[0]}-${detailsMapCenter[1]}`}
                          center={detailsMapCenter}
                          zoom={15}
                          style={{ height: '100%', width: '100%' }}
                          zoomControl={true}
                          className="rounded-lg"
                        >
                          <TileLayer
                            attribution={BASEMAP_ATTRIBUTION}
                            url={BASEMAP_TILE_URL}
                            maxZoom={BASEMAP_MAX_ZOOM}
                          />
                          <Marker position={detailsMapCenter} icon={createCustomIcon()}>
                            <Popup className="custom-popup" closeButton={true}>
                              <div className="text-sm space-y-1 min-w-[180px]">
                                <p className="font-semibold text-foreground">{selectedLocationData.LocationTitle}</p>
                                <p className="text-muted-foreground text-xs">{selectedLocationData.LocationAddress.LocationAddressLineOne}</p>
                              </div>
                            </Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    )}
                  </Card>

                  <div className="space-y-6">
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <IconMapPin className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
                        <div>
                          <p className="text-sm font-medium">Address</p>
                          <p className="text-sm text-muted-foreground">{fullAddress}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <IconPhone className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
                        <div>
                          <p className="text-sm font-medium">Phone</p>
                          <p className="text-sm text-muted-foreground">{selectedLocationData.LocationPhoneNumber}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <IconClock className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
                        <div>
                          <p className="text-sm font-medium">Hours</p>
                          <p className="text-sm text-muted-foreground whitespace-pre-line">{formattedHours}</p>
                        </div>
                      </div>
                    </div>

                    {selectedLocationData.LocationCoupons && (
                      <div>
                        <Badge className="mb-2 bg-emerald-500 hover:bg-emerald-600 text-white">
                          <IconTag className="h-3 w-3 mr-1" />
                          Coupons Available
                        </Badge>
                      </div>
                    )}

                    {selectedLocationData.ads && selectedLocationData.ads.filter(ad => isCurrentlyActive(ad.AdStartDate, ad.AdEndDate)).length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-3">Store Ads</p>
                        <div className="grid grid-cols-2 gap-2">
                          {selectedLocationData.ads
                            .filter(ad => isCurrentlyActive(ad.AdStartDate, ad.AdEndDate))
                            .map((ad, idx) => (
                            <Button
                              key={idx}
                              size="sm"
                              variant="outline"
                              className="w-full"
                              onClick={() => {
                                setSelectedAd(ad);
                                setAdViewerOpen(true);
                              }}
                              disabled={!ad.AdFile?.url}
                            >
                              <IconFileText className="h-4 w-4 mr-2" />
                              {ad.ad_type?.AdType || 'View Ad'}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedLocationData.LocationServices && selectedLocationData.LocationServices.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-3">Services</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedLocationData.LocationServices.map((service, idx) => (
                            <Badge
                              key={idx}
                              variant="default"
                              className={`bg-primary text-primary-foreground ${service.LocationServiceShowMore ? 'cursor-pointer hover:bg-primary/90' : ''}`}
                              onClick={() => {
                                if (service.LocationServiceShowMore) {
                                  setSelectedService(service);
                                  setServiceSheetOpen(true);
                                }
                              }}
                            >
                              {service.LocationServiceShowMore && <IconInfoCircle className="h-3 w-3 mr-1" />}
                              {service.LocationServiceDepartment}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedLocationData.LocationLinks && selectedLocationData.LocationLinks.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-3">Links</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedLocationData.LocationLinks.map((link, idx) => {
                            const linkInfo = getLinkInfo(link.LocationLink);
                            return (
                              <Button
                                key={idx}
                                variant="outline"
                                size="sm"
                                asChild
                              >
                                <a
                                  href={link.LocationLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <linkInfo.icon className="h-4 w-4 mr-2" />
                                  {linkInfo.label}
                                </a>
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                );
              })()}
              </div>
            )}

            {viewMode === 'stores' && (
              <div className="flex-1 overflow-y-auto">
                {storeSelectionContent}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">{storeSelectionContent}</div>
        )}
      </SheetContent>

      <AdViewerSheet
        open={adViewerOpen}
        onOpenChange={setAdViewerOpen}
        ad={selectedAd}
        locationTitle={selectedLocationData?.LocationTitle}
        locationBanner={selectedLocationData?.LocationBanner}
      />

      <ServiceSheet open={serviceSheetOpen} onOpenChange={setServiceSheetOpen}>
        <ServiceSheetContent side="right" className="z-[100] w-full sm:w-[90vw] sm:max-w-2xl p-0 flex flex-col">
          <ServiceSheetHeader className="px-6 pt-6 pb-4 border-b text-left">
            <ServiceSheetTitle className="text-2xl">{selectedService?.LocationServiceDepartment}</ServiceSheetTitle>
          </ServiceSheetHeader>

          {(() => {
            const sections = parseContentSections(selectedService?.LocationServiceDetails);
            const hasMultipleSections = sections.length > 1;

            return (
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                {/* Service Image */}
                {selectedService?.LocationServiceImage?.url && (
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={selectedService.LocationServiceImage.url}
                      alt={selectedService.LocationServiceImage.name || selectedService.LocationServiceDepartment}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                )}

                {/* Service Details - Tabbed if multiple sections, simple if single */}
                {selectedService?.LocationServiceDetails && (
                  hasMultipleSections ? (
                    <Tabs defaultValue={sections[0]?.title} className="w-full">
                      <TabsList className="w-full flex-wrap h-auto gap-1 bg-muted/50 p-1">
                        {sections.map((section, idx) => (
                          <TabsTrigger
                            key={idx}
                            value={section.title}
                            className="flex-1 min-w-fit text-xs sm:text-sm"
                          >
                            {section.title}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      {sections.map((section, idx) => (
                        <TabsContent key={idx} value={section.title} className="mt-4">
                          <div className="prose prose-sm max-w-none">
                            <RichTextRenderer content={section.content} />
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  ) : (
                    <div className="prose prose-sm max-w-none">
                      <RichTextRenderer content={selectedService.LocationServiceDetails} />
                    </div>
                  )
                )}
              </div>
            );
          })()}
        </ServiceSheetContent>
      </ServiceSheet>
    </Sheet>
  );
};
